import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router";

import type { CommunityGroupV2Dto } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useSlug } from "@/hooks/useSlug";
import {
  CommunityGroupForm,
  type CreateOrEditCommunityGroupFormValues,
} from "@/modules/community-groups/components/community-group-editor/CommunityGroupForm";
import {
  createCommunityGroupFormValues,
  createCommunityGroupRequest,
  getMultiProjectGroupType,
} from "@/modules/community-groups/components/community-group-editor/communityGroupFormValues";
import { communityGroupMutations } from "@/queries/community-groups/mutations";
import { useCommunityGroupQueries } from "@/queries/community-groups/queryOptions";
import { usePermissionQueries } from "@/queries/permissions/queryOptions";
import { routes } from "@/routes";

import { useHelpCategoryFormTranslations } from "./useHelpCategoryFormTranslations";

export function CreateHelpCategoryPage(): React.ReactNode {
  const slug = useSlug();
  const navigate = useNavigate();
  const { state } = useLocation();
  const translations = useHelpCategoryFormTranslations(false);
  const duplicateId =
    state && typeof state === "object" && "groupId" in state
      ? (state as { groupId: CommunityGroupV2Dto["id"] }).groupId
      : undefined;
  const queries = useCommunityGroupQueries();
  const groupQuery = useQuery({
    ...queries.details({ groupId: duplicateId! }, { translationMode: "original" }),
    enabled: duplicateId != null,
    gcTime: 0,
  });
  const permissionQueries = usePermissionQueries();
  const defaultsQuery = useQuery(permissionQueries.listDefaultGroups());
  const rolesQuery = useQuery(permissionQueries.listRoles());
  const mutation = communityGroupMutations.useAddGroup();
  const { uploadFormImage, isUploadingImage } = useUploadImage();
  async function onSubmit(values: CreateOrEditCommunityGroupFormValues) {
    let data = createCommunityGroupRequest(values);
    if (values.image[0]) {
      const image = await uploadFormImage(values.image[0]);
      data = { ...data, imageId: image?.id };
    }
    mutation.mutate(
      { ...data, type: values.isRealEstate ? "realEstate" : values.type },
      { onSuccess: (group) => void navigate(routes.helpCategories.details({ slug, id: group.id })) },
    );
  }
  const error = groupQuery.error || defaultsQuery.error || rolesQuery.error;
  if (error) {
    return <ErrorPage error={error} />;
  }
  if (groupQuery.isFetching || defaultsQuery.isFetching || rolesQuery.isFetching) {
    return <FullSizeLoader withPadding />;
  }
  const group = groupQuery.data;

  return (
    <CommunityGroupForm
      contentLanguageId={group?.originalLanguageId}
      type="helpCategory"
      canToggleStatus={!!group?.canDelete && !!group?.systemGroupId}
      isMultiProjectGroup={group?.projectConnectionGroupId !== undefined || group?.systemGroupId !== undefined}
      multiProjectGroupType={getMultiProjectGroupType(group)}
      isEditMode={false}
      defaultValues={createCommunityGroupFormValues({
        roles: rolesQuery.data || [],
        defaultPermissions: defaultsQuery.data || [],
        group,
        type: "helpCategory",
      })}
      isSubmitting={isUploadingImage || mutation.isPending}
      translations={translations}
      onSubmit={onSubmit}
    />
  );
}
